import React from 'react';
import { Table, Container, Button, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { FaCheck, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './calandar.css';

const months = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const maxDaysInYear = 366; // Leap year-safe upper bound

const weekdays = Array.from({ length: maxDaysInYear }, (_, i) => {
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  return days[i % 7];
});

function generateMonthSingleRow(month, year, maxCells, monthMarks = {}) {
  const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
  const startDay = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  // Compute the date number of the last Saturday for this month
  const lastDayOfMonth = new Date(year, monthIndex + 1, 0);
  const lastDayDow = lastDayOfMonth.getDay();
  const lastSaturdayDay = lastDayOfMonth.getDate() - ((lastDayDow + 1) % 7);

  const marks = monthMarks || {};
  const notes = marks.note || {};

  const cells = [
    <td key="month-label" style={{ minWidth: '80px', fontWeight: 'bold' }}>{month}</td>
  ];

  for (let i = 0; i < startDay; i++) {
    cells.push(<td key={`empty-${i}`} style={{ backgroundColor: '#f0f0f0' }}></td>);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dayOfWeek = (startDay + day - 1) % 7;
    const isSunday = dayOfWeek === 0;
    const isSaturday = dayOfWeek === 6;
    const isLastSaturday = isSaturday && day === lastSaturdayDay;

    const dayKey = day.toString();
    const mark = marks[dayKey] ?? marks[day];

    const isPresent = mark === 'present';
    const isLeave = mark === 'leave';

    const className = `position-relative${(isSunday || (isSaturday && !isLastSaturday)) ? ' text-danger' : ''} ${isLeave ? 'mark-leave' : ''}`.trim();

    const cellContent = (
      <div
        className="d-flex justify-content-center align-items-center position-relative"
        style={{ height: '100%' }}
      >
        <span>{day}</span>

        {isPresent && (
          <div
            style={{
              position: 'absolute',
              top: '10%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#4caf50',
              fontWeight: 'bold',
              fontSize: '8px',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '50%',
            }}
          >
            <FaCheck size={8} />
          </div>
        )}
      </div>
    );

    const noteForDay = notes[dayKey] ?? notes[day];

    const cell = (
      <td
        key={`${month}-${day}`}
        className={className}
        style={{
          backgroundColor: isPresent ? '#e8f5e9' : isLeave ? '#fff7e6' : '#fff',
          position: 'relative',
          minWidth: '30px',
          height: '30px',
          textAlign: 'center',
        }}
      >
        {noteForDay ? (
          <OverlayTrigger overlay={<Tooltip id={`tooltip-${month}-${day}`}>{noteForDay}</Tooltip>}>
            {cellContent}
          </OverlayTrigger>
        ) : (
          cellContent
        )}
      </td>
    );

    cells.push(cell);
  }

  const currentCells = startDay + daysInMonth;
  for (let i = currentCells; i < maxCells; i++) {
    cells.push(<td key={`pad-${i}`} style={{ backgroundColor: '#f0f0f0' }}></td>);
  }

  return <tr key={month}>{cells}</tr>;
}

const Calendar = ({ marks = {}, year = new Date().getFullYear(), onYearChange, loading = false }) => {
  const maxCells = Math.max(
    ...months.map((month) => {
      const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
      const startDay = new Date(year, monthIndex, 1).getDay();
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
      return startDay + daysInMonth;
    })
  );

  const handlePreviousYear = () => onYearChange?.(year - 1);
  const handleNextYear = () => onYearChange?.(year + 1);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Container fluid className="calendar-container">
      <div className="d-flex align-items-center my-3" style={{ width: '100%' }}>
        <div className="flex-grow-1 ">
          <Button className='d-flex justify-content-center align-items-center' variant="outline-secondary" onClick={handlePreviousYear} style={{ width: '45px', height: '36px' }}>
            <FaChevronLeft size={14} />
          </Button>
        </div>
        <div className="flex-grow-1 d-flex justify-content-center">
          <p className="mb-0 cal-years" style={{ minWidth: '80px', textAlign: 'center' }}>{year}</p>
        </div>
        <div className="flex-grow-1 d-flex justify-content-end">
          <Button  className='d-flex justify-content-center align-items-center' variant="outline-secondary" onClick={handleNextYear} style={{ width: '45px', height: '36px' }}>
            <FaChevronRight size={14} />
          </Button>
        </div>
      </div>

      <Table bordered size="sm" className="calendar-table">
        <thead>
          <tr>
            <th style={{ minWidth: '80px' }}></th>
            {weekdays.slice(0, maxCells).map((day, idx) => (
              <th key={idx} className={day === 'S' ? 'text-danger' : ''}>{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {months.map((month) =>
            generateMonthSingleRow(month, year, maxCells, marks[month] ? { ...marks[month] } : {})
          )}
        </tbody>
      </Table>
    </Container>
  );
};

export default Calendar;
